// Determine the default landing page for a user based on their scopes.
//
// Portal-only users (e.g. Emily/Roblyn — role=USER with portal:read scope
// but no admin scopes) land on /portal. Everyone else lands on /dashboard.

import type { User } from '../types/auth';
import { EXEC_PORTAL_HOME, EXEC_PORTAL_SCOPE } from '../constants/execPortal';

/** Scopes that indicate admin-level access. */
export const ADMIN_SCOPES: readonly string[] = ['admin:users', 'admin:system', 'admin:api-keys'];

/** The admin panel's home. Routes gated by `hasAdminAccess` fall back here. */
export const ADMIN_HOME = '/dashboard';

/**
 * Whether a user may use the admin panel (the MainLayout routes: /dashboard,
 * /users, /rag, …). ADMIN-role accounts always may; any other role needs at
 * least one explicit admin:* grant in its scopes (scope_overrides), which is
 * also what makes getDefaultLandingPage send that account to /dashboard.
 *
 * This is the single definition the route guard (ProtectedRoute adminOnly)
 * and the landing-page logic share, so a user is never landed on a page the
 * guard would bounce them from.
 */
export function hasAdminAccess(user: User): boolean {
  if (user.role === 'ADMIN') return true;
  return scopesOf(user).some((s) => ADMIN_SCOPES.includes(s));
}

/**
 * The user's explicit scope grants, tolerating a payload that omitted the
 * array. `authService.normalizeUser` already defaults it at the api boundary;
 * this second guard keeps these predicates safe for callers that build a
 * User some other way (tests, cached state), since a throw here happens
 * inside the root render and blanks the whole SPA (kanban 90f3b9d8).
 */
function scopesOf(user: User): string[] {
  return Array.isArray(user?.scopes) ? user.scopes : [];
}

/**
 * Check whether a user is a portal-only user who should bypass
 * the admin dashboard and land directly on /portal.
 *
 * A portal-only user has `portal:read` scope but no admin scopes
 * and is not an ADMIN-role user. This matches internal CSRs like
 * Emily (ER) and Roblyn (RB) who are role=USER.
 */
export function isPortalOnlyUser(user: User): boolean {
  if (hasAdminAccess(user)) return false;
  return scopesOf(user).includes('portal:read');
}

/**
 * A non-admin staff member granted the Quality Co-Pilot (quality:copilot via
 * scope_overrides). These accounts land directly on the Co-Pilot page — the
 * "subset of accounts taken to this page by default" requirement. Admins are
 * excluded (they land on the dashboard) even though hasScope() would pass them;
 * the grant lives in the explicit scopes list, which admin defaults don't carry.
 */
export function isCopilotLandingUser(user: User): boolean {
  if (user.role === 'ADMIN') return false;
  const scopes = scopesOf(user);
  // The read-only viewers (quality:viewer, kanban 808dabf6) land on the same
  // door; what they may do there is the shell's call.
  return scopes.includes('quality:copilot') || scopes.includes('quality:viewer');
}

/**
 * A non-admin staff member granted the Tech Support Co-Pilot
 * (techsupport:copilot via scope_overrides) — e.g. John Kirk. These accounts
 * land directly on his portal page. Admins are excluded (they land on the
 * dashboard) even though hasScope() would pass them; the grant lives in the
 * explicit scopes list, which admin defaults don't carry.
 */
export function isTechSupportCopilotUser(user: User): boolean {
  if (user.role === 'ADMIN') return false;
  return scopesOf(user).includes('techsupport:copilot');
}

/**
 * A non-admin manager/exec granted read access to the Quality Signals reader
 * area (signals:read via scope_overrides) — e.g. Dr. Ravi Joshi. These accounts
 * land on the portal Quality Signals reports feed (/portal/signals). Admins are
 * excluded (they land on /dashboard) even though hasScope() would pass them; the
 * grant lives in the explicit scopes list, which admin defaults don't carry.
 * This is a SOFT default (not forced): the account keeps its normal portal
 * access (customers/orders), so an explicit returnUrl / deep link still wins
 * (see pickPostLoginDestination).
 */
export function isSignalsPortalUser(user: User): boolean {
  if (user.role === 'ADMIN') return false;
  return scopesOf(user).includes('signals:read');
}

/**
 * A leadership account granted the executive portal door (exec:portal via
 * scope_overrides) — PJ Murphy and Ravi Joshi. Unlike the other door
 * predicates this one deliberately INCLUDES admins: PJ and Ravi are ADMIN-role
 * accounts, and the ask (2026-09-17 / 2026-09-21 check-ins) is that their
 * login goes straight to the curated portal, not the admin dashboard. The
 * grant lives in the explicit scopes list, which admin defaults never carry,
 * so an admin WITHOUT the grant (Scott) is unaffected. Soft default: a deep
 * link / returnUrl still wins (see pickPostLoginDestination).
 */
export function isExecPortalUser(user: User): boolean {
  return scopesOf(user).includes(EXEC_PORTAL_SCOPE);
}

/**
 * Whether the portal mounts the critical red-flag bar for this user.
 *
 * Owner ruling on review rwusd8 (2026-09-28): the bar is the CSRs' queue, so
 * someone who is not a CSR does not see it. Admins / managers looking at the
 * portal and executive readers (PJ, Ravi) are withheld; CSRs keep it, and a
 * salesperson keeps the alerts addressed to their own code (a critical alert
 * shows nowhere else). The server applies the same rule on
 * GET /notifications/critical (services/notifications/critical_bar_audience.py).
 */
export function seesCriticalAlertBar(user: User | null): boolean {
  if (!user) return false;
  if (user.role === 'ADMIN' || user.role === 'MANAGER') return false;
  return !isExecPortalUser(user);
}

/**
 * The dedicated CPLEX operator account. When this user logs in we drop them
 * straight onto the full-bleed Sankey workspace so they never have to click
 * through the admin panel.
 */
const CPLEX_OPERATOR_EMAIL = 'cplex@chemmasters.net';

/**
 * Return the default landing path for a user after login.
 *
 * Precedence (high → low):
 * - Forced single-purpose accounts (cplex@ operator, and the Tech Support
 *   Co-Pilot / John Kirk) → their dedicated page (see getForcedLandingPage)
 * - Executive portal readers (exec:portal, any role) → /portal/roadmap
 * - Quality Co-Pilot users → /portal/quality-copilot
 * - Portal-only users (have portal:read, no admin scopes) → /portal
 * - Everyone else (admins, salespeople, etc.) → /dashboard
 */
export function getDefaultLandingPage(user: User): string {
  const forced = getForcedLandingPage(user);
  if (forced) return forced;
  if (isExecPortalUser(user)) return EXEC_PORTAL_HOME;
  if (isCopilotLandingUser(user)) return '/portal/quality-copilot';
  if (isSignalsPortalUser(user)) return '/portal/signals';
  if (isPortalOnlyUser(user)) return '/portal';
  return ADMIN_HOME;
}

/**
 * Where a user who is NOT allowed on an admin-only route should be sent, or
 * null when there is nowhere sensible to send them.
 *
 * Non-admins normally have a home of their own (a portal door, /portal), so
 * the guard redirects there. The null case is an account with no admin grant
 * AND no portal:read (a scope_overrides removal, or a role the defaults don't
 * cover): its default landing would be /dashboard, the very route being
 * guarded, so redirecting would loop. The caller renders a "not authorized"
 * surface instead.
 */
export function getNonAdminRedirect(user: User): string | null {
  const destination = getDefaultLandingPage(user);
  return destination === ADMIN_HOME ? null : destination;
}

/**
 * Return a landing path that should win over any explicit returnUrl /
 * router-state target, or null if the user has no forced destination.
 *
 * Single-purpose accounts have no business anywhere else, so a stale bookmark
 * or a deep-link returnUrl appended by ProtectedRoute must NOT steal their
 * routing. The post-login navigate target prefers returnUrl over the default
 * landing page (pickPostLoginDestination), which is the bug fixed for cplex@
 * in PR #1210 — and for John Kirk here:
 *
 *   - the cplex@ operator → always /cplex/sankey, and
 *   - non-admin Tech Support Co-Pilot users (John Kirk) → always
 *     /portal/tech-support-copilot. His account is door-locked to the co-pilot
 *     — every /api/admin call 403s — so a returnUrl to /dashboard (a common
 *     stale bookmark) is a dead end that strands him on an unusable page.
 *
 * Quality Co-Pilot is intentionally left as a soft default (not forced); the
 * `!isCopilotLandingUser` guard preserves its precedence for the contrived
 * account that somehow holds both co-pilot grants.
 */
export function getForcedLandingPage(user: User): string | null {
  if (user.email?.toLowerCase() === CPLEX_OPERATOR_EMAIL) return '/cplex/sankey';
  if (isTechSupportCopilotUser(user) && !isCopilotLandingUser(user)) {
    return '/portal/tech-support-copilot';
  }
  return null;
}

/**
 * Decide where to navigate after a successful login.
 *
 * Precedence (high → low):
 *   1. A forced destination on the user (operator accounts).
 *   2. An explicit returnUrl supplied by ProtectedRoute (?returnUrl=…)
 *      or router state (location.state.from.pathname).
 *   3. The user's default landing page.
 *
 * Extracted from LoginPage.tsx so the precedence is unit-testable as a
 * pure function without rendering the component.
 */
export function pickPostLoginDestination(
  user: User,
  explicitDestination: string | null | undefined,
): string {
  const forced = getForcedLandingPage(user);
  if (forced) return forced;
  if (explicitDestination) return explicitDestination;
  return getDefaultLandingPage(user);
}

/**
 * Paths served outside the React router (by the api or straight from nginx).
 * The SPA must leave the router and do a full navigation to reach them,
 * otherwise the client-side router renders NotFound. Each of these gates
 * sends anonymous visitors to /login?returnUrl=<path> and relies on this
 * list to get them back after login:
 *   - /kb-explorer/  the hosted Knowledge Bank Explorer (app/routes/kb_explorer.py)
 *   - /metabase/     Metabase behind the nginx auth_request gate
 *                    (@metabase_login_redirect in nginx/<env>/nginx.conf)
 */
export const SERVER_HOSTED_PATH_PREFIXES: readonly string[] = ['/kb-explorer', '/metabase'];

export function isServerHostedPath(path: string): boolean {
  return SERVER_HOSTED_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}
